import * as _ from 'lodash';
import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetSnippetsParams, SnippetPackagesResponse } from '../../../../shared/snippets';

import { getSnippetsBaseQueryKey } from './shared';
import type { CompareSnippetsParams } from './shared';

export type UseSnippetPackagesOptions = {
  userRevisionLocator: string;
  params?: GetSnippetsParams;
  compare?: CompareSnippetsParams;
};

/**
 * Function that fetches snippet packages based on the given options.
 */
export function getSnippetPackages({ userRevisionLocator, params, compare }: UseSnippetPackagesOptions) {
  return compare
    ? get<SnippetPackagesResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/compare/${nuc(compare.olderRevisionLocator)}/${compare.status}/packages`, false, params)
    : get<SnippetPackagesResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/packages`, false, params);
}

/**
 * Returns the query key for the snippet packages based on the given options.
 */
export const getSnippetPackagesQueryKey = ({
  userRevisionLocator,
  params,
  compare,
}: UseSnippetPackagesOptions): QueryKey => {
  return compare
    ? [...getSnippetsBaseQueryKey(userRevisionLocator), 'compare', compare.olderRevisionLocator, compare.status, 'packages', params]
    : [...getSnippetsBaseQueryKey(userRevisionLocator), 'packages', params];
};

/**
 * useSnippetPackages is a hook that fetches snippet packages.
 */
export default function useSnippetPackages(options: UseSnippetPackagesOptions) {
  return useQuery<SnippetPackagesResponse>({
    queryKey: getSnippetPackagesQueryKey(options),
    queryFn: () => getSnippetPackages(options),
    //  Path must exist in the request, so don't fetch if we don't have a path.
    enabled: !_.isNil(options.params?.path),
  });
}
